import * as React from "react";
import cn from "classnames";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import DataTableColumn from "./data-table-column";
import DataTableToggle from "./data-table-toggle";
import DataTableToggleRowFormatter from "./data-table-toggle-row-formatter";
import {
  DataTableComposition,
  DataTableProps,
  RowFormatterType,
} from "./interfaces";

import "./data-table.scss";

type RowMapType<T> = (item: T, index: number) => React.ReactNode;

type HeaderAPI = {
  fieldName?: string;
};

export type DataTableRef = HTMLTableElement;

const DefaultRowFormatter: RowFormatterType<any> = ({
  index,
  cells,
  getBlocksWith,
}) => (
  <tr
    key={`data_row_${index}`}
    className={getBlocksWith("__body-row")}
    data-auto="table-row"
  >
    {cells}
  </tr>
);

const DataTable = React.forwardRef<DataTableRef, DataTableProps<any>>(
  function DataTable<T>(
    {
      dataGetter,
      children,
      rowFormatter = DefaultRowFormatter,
      caption,
      className,
    }: DataTableProps<T>,
    ref: React.Ref<HTMLTableElement>,
  ) {
    const getBlocksWith = createBemBlockBuilder(["eb-data-table", className]);

    const renderRow: RowMapType<any> = (item, index) => {
      const cells = React.Children.map(children, (child) => {
        if (!child) {
          return null;
        }
        const { tagName, fieldName } = child.props;
        const getCellBlocksWith = createBemBlockBuilder([
          child.props.className,
        ]);
        const dataClasses = cn(
          getBlocksWith("__cell"),
          getCellBlocksWith("__cell"),
        );
        // @ts-expect-error c.type.displayName is not a member of the React.Element type.
        if (child.type.displayName !== "DataTable.Column") {
          console.warn(
            "DataTable only uses children of type DataTable.Column. Other component types will be ignored.",
          );
          return null;
        }
        const Tag = tagName || "td";
        return (
          <Tag key={fieldName} className={dataClasses} data-auto="table-data">
            {child.props.formatter(item)}
          </Tag>
        );
      });
      return rowFormatter({ item, cells, index, getBlocksWith });
    };

    const renderRows = () => {
      let index = 0;
      const rows = [];
      let res;
      let row: T | boolean = true;
      while (row) {
        row = dataGetter(index);
        res = row ? renderRow(row, index) : row;
        if (row) {
          rows.push(res);
        }
        index += 1;
      }
      return rows;
    };

    // render headers, account for sorting
    const renderHeader = (
      render: (arg0: HeaderAPI) => React.ReactNode,
      fieldName: string,
    ) => {
      return render({ fieldName });
    };

    let hasHeaders = false;
    const headers = React.Children.map(children, (child) => {
      if (!child) {
        return null;
      }
      const { header, headerClassName, fieldName } = child.props;
      const getHeaderBlocksWith = createBemBlockBuilder([headerClassName]);
      const headerClasses = cn(
        getBlocksWith("__header-cell"),
        getHeaderBlocksWith("__header-cell"),
      );
      if (!header) {
        return <th key={fieldName}></th>;
      }
      hasHeaders = true;
      return (
        <th
          key={fieldName}
          className={headerClasses}
          data-auto="table-column-header"
        >
          {renderHeader(header, fieldName)}
        </th>
      );
    });

    //render footers
    const renderFooter = (
      render: (arg0: HeaderAPI) => React.ReactNode,
      fieldName: string,
    ) => {
      return render({ fieldName });
    };

    let hasFooters = false;
    const footers = React.Children.map(children, (child) => {
      if (!child) {
        return null;
      }
      const { footer, fieldName, footerClassName } = child.props;
      const getFooterBlocksWith = createBemBlockBuilder([footerClassName]);
      const footerClasses = cn(
        getBlocksWith("__footer-cell"),
        getFooterBlocksWith("__footer-cell"),
      );
      if (!footer) {
        return <td key={fieldName}></td>;
      }
      hasFooters = true;
      return (
        <td
          key={fieldName}
          className={footerClasses}
          data-auto="table-column-footer"
        >
          {renderFooter(footer, fieldName)}
        </td>
      );
    });

    return (
      <div className={getBlocksWith()} tabIndex={0} data-auto="table-wrapper">
        <table
          className={getBlocksWith("__table")}
          data-auto="table-self"
          ref={ref}
        >
          {caption && (
            <caption
              className={getBlocksWith("__caption")}
              data-auto="table-caption"
            >
              {caption}
            </caption>
          )}
          {hasHeaders && (
            <thead
              className={getBlocksWith("__header-section")}
              data-auto="table-headers"
            >
              <tr className={getBlocksWith("__header-row")}>{headers}</tr>
            </thead>
          )}
          <tbody className={getBlocksWith("__body")} data-auto="table-body">
            {renderRows()}
          </tbody>
          {hasFooters && (
            <tfoot
              className={getBlocksWith("__footer-section")}
              data-auto="table-footers"
            >
              <tr className={getBlocksWith("__footer-row")}>{footers}</tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  },
) as DataTableComposition<DataTableRef, DataTableProps<any>>;

DataTable.displayName = "DataTable";
DataTable.Column = DataTableColumn;
DataTable.Column.displayName = "DataTable.Column";
DataTable.Toggle = DataTableToggle;
DataTable.Toggle.displayName = "DataTable.Toggle";
DataTable.ToggleRowFormatter = DataTableToggleRowFormatter;

export default DataTable;

export * from "./interfaces";
